import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import Button from "../../../../components/buttons/buttons";
import PayoutDecisionModal from "../../../../components/modals/admin/payoutDecisionModal";
import MarkPayoutPaidModal from "../../../../components/modals/admin/markPayoutPaidModal";
import {
  approveRevenuePayout,
  rejectRevenuePayout,
  showARevenuePayout,
  markRevenuePayoutPaid,
} from "../../../../redux/revenuePayout/revenuePayoutActions";

function numberWithCommas(x) {
  return x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export default function PayOutRequest() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id, request_id } = useParams();

  // "approve" | "reject" while the reason modal is open
  const [decision, setDecision] = useState(null);
  const [openPayout, setOpenPayout] = useState(false);

  const { singleRevenuePayout, isLoading, approving } = useSelector(
    (state) => state.revenuePayout,
  );

  // Ignore a payout left in the store from a previously opened request
  const payoutRequest =
    singleRevenuePayout?.id === request_id ? singleRevenuePayout : null;
  const currency =
    payoutRequest?.currency_code ||
    payoutRequest?.revenue_wallet?.currency_code ||
    "NGN";
  const status = (
    payoutRequest?.status || (payoutRequest?.paid_out ? "paid" : "pending")
  ).toLowerCase();
  // Only a pending request can still be accepted or rejected
  const canDecide = !!payoutRequest && status === "pending";
  // An accepted request is paid out next
  const canPayOut =
    !!payoutRequest && ["approved", "accepted"].includes(status);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (request_id) {
      dispatch(showARevenuePayout(request_id));
    }
  }, [dispatch, request_id]);

  const handleDecision = async (reason) => {
    const response = await dispatch(
      decision === "approve"
        ? approveRevenuePayout({ id: request_id, approval_reason: reason })
        : rejectRevenuePayout({ id: request_id, rejection_reason: reason }),
    );
    if (response?.status === "success") {
      setDecision(null);
      dispatch(showARevenuePayout(request_id));
    }
  };

  const handlePayOut = async (details) => {
    const response = await dispatch(
      markRevenuePayoutPaid({ id: request_id, ...details }),
    );
    if (response?.status === "success") {
      setOpenPayout(false);
      dispatch(showARevenuePayout(request_id));
    }
  };

  return (
    <>
      <MarkPayoutPaidModal
        show={openPayout}
        onClose={() => setOpenPayout(false)}
        onSubmit={handlePayOut}
        submitting={approving}
      />

      <PayoutDecisionModal
        action={decision}
        onClose={() => setDecision(null)}
        onSubmit={handleDecision}
        submitting={approving}
      />

      <div data-test="shipper-payout-request-page">
        <div className="mb-6">
          <Button.Secondary
            title="← Back to Shipper"
            onClick={() => navigate(`/admin/revenue/shipper/${id}/payouts`)}
          />
        </div>

        <div className="border border-[#E4E4F3] dark:border-none bg-white dark:bg-[#121212] rounded-md py-5 px-4 sm:px-8">
          <div className="border-b border-[#E4E4F3] dark:border-[#505050] py-3 flex justify-between items-center">
            <h3 className="text-lg font-medium">
              Pay Out Request:{" "}
              {isLoading && !payoutRequest
                ? "Loading..."
                : payoutRequest?.request_id || payoutRequest?.id || "N/A"}
            </h3>

            {canPayOut ? (
              <Button.Primary
                title="Payout"
                onClick={() => setOpenPayout(true)}
                disabled={approving}
                className="px-5"
                type="button"
              />
            ) : (
              <div className="flex gap-4 items-center">
                <Button.Secondary
                  title="Reject Request"
                  onClick={() => setDecision("reject")}
                  disabled={!canDecide || approving}
                  className="px-5"
                  type="button"
                />

                <Button.Primary
                  title="Accept Request"
                  onClick={() => setDecision("approve")}
                  disabled={!canDecide || approving}
                  className="px-5"
                  type="button"
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-6">
            <div>
              <label className="text-[#9CA3AF] dark:text-[#BCBCBC] text-sm mb-2 block">
                Request ID
              </label>
              <p className="text-[#151515] dark:text-white text-sm">
                {payoutRequest?.request_id || payoutRequest?.id || "N/A"}
              </p>
            </div>

            <div>
              <label className="text-[#9CA3AF] dark:text-[#BCBCBC] text-sm mb-2 block">
                Shipper Name
              </label>
              <p className="text-[#151515] dark:text-white text-sm">
                {payoutRequest?.shipper?.name ||
                  payoutRequest?.shipper_name ||
                  "N/A"}
              </p>
            </div>

            <div>
              <label className="text-[#9CA3AF] dark:text-[#BCBCBC] text-sm mb-2 block">
                Amount
              </label>
              <p className="text-blue-600 dark:text-blue-400 text-lg font-semibold">
                {currency} {numberWithCommas(payoutRequest?.amount || 0)}
              </p>
            </div>

            <div>
              <label className="text-[#9CA3AF] dark:text-[#BCBCBC] text-sm mb-2 block">
                Request Date
              </label>
              <p className="text-[#151515] dark:text-white text-sm">
                {payoutRequest?.request_date || payoutRequest?.created_at
                  ? moment(
                      payoutRequest?.request_date || payoutRequest?.created_at,
                    ).format("DD MMM YYYY, hh:mm A")
                  : "N/A"}
              </p>
            </div>

            <div>
              <label className="text-[#9CA3AF] dark:text-[#BCBCBC] text-sm mb-2 block">
                Status
              </label>
              <span className="inline-flex rounded-full px-3 py-1 text-xs font-semibold bg-yellow-100 text-yellow-800 capitalize">
                {payoutRequest ? status : "N/A"}
              </span>
            </div>

            <div>
              <label className="text-[#9CA3AF] dark:text-[#BCBCBC] text-sm mb-2 block">
                Shipper ID
              </label>
              <p className="text-[#151515] dark:text-white text-sm">
                {payoutRequest?.shipper_id || id}
              </p>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
